import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { useNotify } from 'react-admin';
import { ShowDynamics, logger } from '@musedrops/shared';
import {
  getAllShowDynamics,
  createShowDynamics,
  updateShowDynamics,
  deleteShowDynamics,
  countShowsWithDynamics,
} from '../../../data/showDynamics';
import { TagsInput } from '../../../shared/components/TagsInput';

interface DynamicsWithUsage extends ShowDynamics {
  showCount: number;
}

export const DynamicsSettings: React.FC = () => {
  const notify = useNotify();
  const [dynamics, setDynamics] = useState<DynamicsWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state - shared for create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingDynamics, setEditingDynamics] =
    useState<DynamicsWithUsage | null>(null);
  const [dialogKey, setDialogKey] = useState('');
  const [dialogGoodFor, setDialogGoodFor] = useState('');
  const [dialogInstructions, setDialogInstructions] = useState('');
  const [dialogTags, setDialogTags] = useState<string | null>('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDynamics, setDeletingDynamics] =
    useState<DynamicsWithUsage | null>(null);

  useEffect(() => {
    loadDynamics();
  }, []);

  const loadDynamics = async () => {
    try {
      setLoading(true);
      setError(null);

      const allDynamics = await getAllShowDynamics();

      // Get usage counts for each dynamics
      const dynamicsWithUsage = await Promise.all(
        allDynamics.map(async dyn => ({
          ...dyn,
          showCount: await countShowsWithDynamics(dyn.key),
        }))
      );

      setDynamics(dynamicsWithUsage);
    } catch (err) {
      logger.error('Failed to load show dynamics:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load show dynamics'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setEditingDynamics(null);
    setDialogKey('');
    setDialogGoodFor('');
    setDialogInstructions('');
    setDialogTags('');
    setDialogOpen(true);
  };

  const handleEditClick = (dyn: DynamicsWithUsage) => {
    setDialogMode('edit');
    setEditingDynamics(dyn);
    setDialogKey(dyn.key);
    setDialogGoodFor(dyn.goodFor);
    setDialogInstructions(dyn.instructions);
    setDialogTags(dyn.tags || '');
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    if (dialogMode === 'create') {
      try {
        await createShowDynamics({
          key: dialogKey,
          goodFor: dialogGoodFor,
          instructions: dialogInstructions,
          tags: dialogTags || null,
        });
        notify('Dynamics created successfully', { type: 'success' });
        setDialogOpen(false);
        setDialogKey('');
        setDialogGoodFor('');
        setDialogInstructions('');
        setDialogTags('');
        loadDynamics();
      } catch (err) {
        notify(
          err instanceof Error ? err.message : 'Failed to create dynamics',
          {
            type: 'error',
          }
        );
      }
    } else {
      if (!editingDynamics) return;

      try {
        await updateShowDynamics(editingDynamics.id, {
          key: dialogKey,
          goodFor: dialogGoodFor,
          instructions: dialogInstructions,
          tags: dialogTags || null,
        });
        notify('Dynamics updated successfully', { type: 'success' });
        setDialogOpen(false);
        setEditingDynamics(null);
        setDialogKey('');
        setDialogGoodFor('');
        setDialogInstructions('');
        setDialogTags('');
        loadDynamics();
      } catch (err) {
        notify(
          err instanceof Error ? err.message : 'Failed to update dynamics',
          {
            type: 'error',
          }
        );
      }
    }
  };

  const handleDeleteClick = (dyn: DynamicsWithUsage) => {
    setDeletingDynamics(dyn);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDynamics) return;

    try {
      await deleteShowDynamics(deletingDynamics.id);
      notify('Dynamics deleted successfully', { type: 'success' });
      setDeleteDialogOpen(false);
      setDeletingDynamics(null);
      loadDynamics();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to delete dynamics', {
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Good For</TableCell>
              <TableCell>Used By Shows</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={handleCreateClick}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dynamics.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No dynamics defined yet. Click + to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dynamics.map(dyn => (
                <TableRow
                  key={dyn.id}
                  onClick={() => handleEditClick(dyn)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <code>{dyn.key}</code>
                  </TableCell>
                  <TableCell>{dyn.goodFor}</TableCell>
                  <TableCell>
                    {dyn.showCount > 0 ? (
                      <Typography variant="body2">{dyn.showCount}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(dyn);
                      }}
                      disabled={dyn.showCount > 0}
                      title={
                        dyn.showCount > 0
                          ? `Cannot delete - used by ${dyn.showCount} show(s)`
                          : 'Delete dynamics'
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'edit' ? 'Edit Dynamics' : 'Create Dynamics'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key"
            fullWidth
            value={dialogKey}
            onChange={e => setDialogKey(e.target.value)}
            helperText="Unique identifier, e.g., 'teller-sidekick'"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Good For"
            fullWidth
            value={dialogGoodFor}
            onChange={e => setDialogGoodFor(e.target.value)}
            helperText="One sentence describing when this dynamics works well"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={dialogInstructions}
            onChange={e => setDialogInstructions(e.target.value)}
            helperText="Detailed instructions for AI on how to implement this interaction pattern"
            required
            sx={{ mb: 2 }}
          />
          <TagsInput
            value={dialogTags}
            onChange={setDialogTags}
            label="Tags (Optional)"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disabled={!dialogKey || !dialogGoodFor || !dialogInstructions}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Dynamics</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the dynamics{' '}
            <code>{deletingDynamics?.key}</code>?
          </Typography>
          {deletingDynamics && deletingDynamics.showCount > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This dynamics is used by {deletingDynamics.showCount} show(s) and
              cannot be deleted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={!!deletingDynamics && deletingDynamics.showCount > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
